import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

const EMBED = `<!-- Copy yours from the form's Share tab: it carries your form's id. The script and the form are served by the Revvly dashboard, app.revvly.ai. -->
<div data-revvly-form="Xk2mQ8vT1nR4pLw7Za"></div>
<script src="https://app.revvly.ai/forms-embed.js" async></script>`;

const THEMED_EMBED = `<div
  data-revvly-form="Xk2mQ8vT1nR4pLw7Za"
  data-revvly-theme="dark"
  data-revvly-background="transparent"
  data-revvly-text="#FFFFFF"
  data-revvly-label="#D1D5DB"
  data-revvly-muted="#9CA3AF"
  data-revvly-input-background="#000000"
  data-revvly-input-border="#374151"
  data-revvly-input-focus-border="#4B5563"
  data-revvly-accent="#3B82F6"
  data-revvly-accent-text="#FFFFFF"
  data-revvly-error="#EF4444"
  data-revvly-radius="6"
  data-revvly-button-radius="24"
></div>`;

const THEMED_JSON = `<div
  data-revvly-form="Xk2mQ8vT1nR4pLw7Za"
  data-revvly-theme='{"scheme":"dark","background":"transparent","accent":"#3B82F6"}'
  data-revvly-button-background="linear-gradient(90deg, #3B82F6, #8B5CF6)"
></div>`;

const AGENT_EMBED_PROMPT = `Add my Revvly form to this website with Revvly's embed snippet, then offer to style it.

1. Put this where the form should appear. Use my form's real id, copied from the form's Share tab in Revvly (the script address is the Revvly dashboard, app.revvly.ai):

<div data-revvly-form="FORM_ID"></div>
<script src="https://app.revvly.ai/forms-embed.js" async></script>

2. Before you finish, ASK ME this, and wait for my answer:
"The embedded form is light with a mint button by default. Do you want it to match this site (colours, corner radius, dark or light), or keep the default?"
Do not restyle the form unless I say yes, and do not guess at colours.
- If I say keep the default, stop here.
- If I say match the site, read this site's stylesheet or design tokens and find: the page background, the text colour, the input border colour, the accent (primary button) colour, the text colour on that button, the error colour, the corner radius of inputs and of buttons, and the font. Show me what you found as a short list and ask me to confirm or change each value before you use it.

3. Add the confirmed values to the same <div> as data-revvly-* attributes. Only these attributes exist, so do not invent others:
- data-revvly-theme: "dark" or "light" (light is the default). Set it to match the page behind the form.
- data-revvly-background: the page behind the form, or "transparent" so the form sits directly on this site's own background.
- data-revvly-card: the form's own surface. Leave it out for no card.
- data-revvly-text, data-revvly-label, data-revvly-muted: title and answers, field labels, and help text plus placeholders.
- data-revvly-input-background, data-revvly-input-border, data-revvly-input-focus-border.
- data-revvly-accent: the submit button, focus ring, links and selected options. data-revvly-accent-text: the text on the button.
- data-revvly-button-background: use only when the button is not a flat accent, as a colour or a linear-gradient(...) of colour stops.
- data-revvly-error, data-revvly-radius (inputs, in pixels), data-revvly-button-radius (the button, in pixels).
- data-revvly-font: a font family the visitor's device already has, such as "Inter, system-ui, sans-serif". The form runs in its own frame and cannot load this site's font files, so tell me if the site uses a webfont.
Values must be hex, rgb(), hsl() or "transparent" for colours, and plain numbers for radii. Anything else is ignored. Set data-revvly-theme to "dark" whenever the page behind the form is dark, even if I did not mention it.

4. Check the result in a browser: the form has no white box on a dark page, text is readable against its background, error text is visible, and the submit button label is readable. Tell me what you changed and which values came from the site and which from me.`;

const AGENT_RESTYLE_PROMPT = `My website already embeds a Revvly form (a <div data-revvly-form="..."> followed by the forms-embed.js script). Help me change how it looks.

First ask me: "Do you want the form to follow this site's colours and corner radius, a specific palette you will give me, or keep the default look?" Wait for the answer. If it is the default, change nothing.

Then set data-revvly-* attributes on that <div>, without touching the script tag or the form itself: data-revvly-theme ("dark" or "light"), data-revvly-background (a colour or "transparent"), data-revvly-text, data-revvly-label, data-revvly-muted, data-revvly-input-background, data-revvly-input-border, data-revvly-input-focus-border, data-revvly-accent, data-revvly-accent-text, data-revvly-button-background (colour or linear-gradient), data-revvly-error, data-revvly-radius, data-revvly-button-radius, data-revvly-font. Show me the values before applying them. If something does not change, check the value is a hex, rgb(), hsl() or "transparent" colour, because anything else is ignored.`;

const TEMPLATE = `Hi {{field.first_name}},

Thanks for getting in touch about **{{field.company}}**. We will reply within one working day.

The {{form.name}} team`;

const FIELDS: [string, string][] = [
  ['Short text, Long text', 'Free text, with an optional maximum length.'],
  [
    'Formatted text',
    'Bold, italics, links and lists. Stored as sanitised HTML; scripts and styles are removed.',
  ],
  ['Email, Phone, Website', 'Checked for shape. A website without https:// gets it added.'],
  ['Number, Date', 'A number, or a date as YYYY-MM-DD.'],
  [
    'Single choice, Dropdown',
    'One of the options you list. The answer is stored as the option label.',
  ],
  ['Multiple choice', 'Any of the options you list.'],
  ['Consent checkbox', 'A box to tick, for terms or marketing consent.'],
  [
    'File upload',
    '1 to 5 files of up to 30 MB each, optionally limited to images, PDFs, documents, spreadsheets, presentations or text.',
  ],
  [
    'Hidden value',
    'Not shown. Filled from a parameter in the page address, e.g. utm_source, so you know which campaign brought each submission.',
  ],
  ['Heading, Paragraph', 'Layout only: titles and explanatory text between questions.'],
];

const STEPS: [string, string][] = [
  [
    'Create a lead',
    'Adds a lead to the pipeline and stage you choose, with the answers on it. If the email already belongs to a contact, the lead goes on that contact and their company. Otherwise the company is found by the website you collected, or by the email domain (never for Gmail-style addresses), or created, and a new contact is added. Map the first name, last name, company name and company website to any question in the step, or give those questions a role and the step picks them up.',
  ],
  [
    'Email Revvly users',
    'Sends the people you pick every answer and a link to the submission. Only current members of your workspace receive it.',
  ],
  [
    'Send an email',
    'Sends your own message to the person who filled in the form, to fixed addresses, or both. Send it from Revvly, from your shared mailbox, or from your own connected mailbox.',
  ],
  [
    'Post to Slack',
    'Posts the answers to a channel. Any public channel works; for a private channel, invite the Revvly app to it first.',
  ],
];

export function FormsGuide() {
  useDocsPage('Collect answers with Forms — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Forms</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Collect answers with <em>Forms</em>.
        </h1>
        <p className="dp-oneliner">
          Build a contact, demo-request or application form, put it on your site or share its link,
          and have every submission create a lead, tell your team and reply to the person, without
          anyone copying it across by hand.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="before">Before you start</AnchorHeading>
        <p className="dp-section-sub">
          Forms is in the sidebar under <strong>Library</strong>, in the Workspace section, next to
          Documents and Templates. Forms is turned on per workspace; if it is not listed under
          Library, ask us to enable it. Everyone in a workspace with Forms can build forms and read
          their submissions.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="build">Step 1: Build the form</AnchorHeading>
        <UiPath
          steps={['Library', 'Forms', 'New form', 'Build']}
          note="Add questions from the left, edit each one on the right, and see the form as people will see it in the preview. Changes are saved as you go."
        />
        <div className="dp-fields-table api-field-table-mb">
          {FIELDS.map(([k, v]) => (
            <div className="dp-field-row" key={k}>
              <div className="dp-field-k">{k}</div>
              <div className="dp-field-v">{v}</div>
            </div>
          ))}
        </div>
        <p className="dp-section-sub">
          Give the questions that identify the person a <strong>role</strong> (contact email, first
          name, last name, full name, phone, company name, company website). Steps use roles to find
          &ldquo;the email&rdquo; or &ldquo;the company&rdquo; without you wiring each one by hand.
          Each role can be used by one question per form.
        </p>
        <p className="dp-section-sub">
          Under form settings you choose the submit button&rsquo;s label, the thank-you message, and
          an optional https:// address to send people to afterwards.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="actions">Step 2: Choose what happens on each submission</AnchorHeading>
        <UiPath
          steps={['Library', 'Forms', 'Your form', 'Actions', 'Add step']}
          note="Steps run in order, straight after each submission, in the background: the person filling the form never waits for them."
        />
        <div className="dp-fields-table api-field-table-mb">
          {STEPS.map(([k, v]) => (
            <div className="dp-field-row" key={k}>
              <div className="dp-field-k">{k}</div>
              <div className="dp-field-v">{v}</div>
            </div>
          ))}
        </div>
        <p className="dp-section-sub">
          Email and Slack messages can quote answers with tokens, filled in as plain text so nobody
          can inject formatting, links or mentions:
        </p>
        <pre className="api-code">{TEMPLATE}</pre>
        <p className="dp-section-sub">
          <span className="api-inline">{'{{field.<key>}}'}</span> is an answer (each
          question&rsquo;s key is shown in its settings),{' '}
          <span className="api-inline">{'{{form.name}}'}</span> the form&rsquo;s name, and{' '}
          <span className="api-inline">{'{{submission.url}}'}</span> a link to the submission in
          Revvly.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            A step that fails is retried automatically over about 45 minutes, and the steps after it
            wait so they still run in order. If it still fails, its{' '}
            <strong>If this step fails</strong> setting decides:{' '}
            <em>Carry on with the next step</em>, or <em>Stop the remaining steps</em>. You can
            retry failed steps from the submission later; steps that already worked are not
            repeated.
          </span>
        </div>
        <Figure
          title="What happens to a submission"
          caption="The submission is saved first and the steps run afterwards, so a failing step never costs you the answers."
        >
          <Flow
            steps={[
              {
                title: 'Published version',
                text: 'The visitor sees only a published version, never your draft.',
              },
              {
                title: 'Visitor submits',
                text: 'Saved with the version it was made on.',
                tone: 'accent',
              },
              {
                title: 'Steps run in order',
                text: 'In the background. The visitor never waits.',
              },
              {
                fork: [
                  { title: 'Done', text: 'Every step ran.', tone: 'accent' },
                  {
                    title: 'A step fails',
                    text: 'Retried over about 45 minutes, then your setting decides: carry on or stop.',
                    tone: 'warn',
                  },
                ],
              },
            ]}
          />
        </Figure>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            An email to the person who filled in the form goes to whatever address they typed. Quote
            short answers such as a name or a company in it, not long free text, so the form cannot
            be used to send someone else a message of the submitter&rsquo;s choosing.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="publish">Step 3: Publish</AnchorHeading>
        <p className="dp-section-sub">
          Nobody sees your edits until you press <strong>Publish</strong>. Publishing checks that
          the form is complete: every step is set up, every token names a real question, the
          pipeline, stage and people still exist, the mailbox is connected and Revvly can post to
          the Slack channel. Anything missing is listed so you can fix it.
        </p>
        <p className="dp-section-sub">
          Each publish is a new version. Submissions keep the version they were made on, so you can
          rename or remove a question later without losing or mislabelling earlier answers. Archive
          a form to take it offline; restore it to bring it back.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="share">Step 4: Share it</AnchorHeading>
        <UiPath steps={['Library', 'Forms', 'Your form', 'Share']} />
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Link</div>
            <div className="dp-field-v">
              A page hosted by Revvly. Send it by email, put it in a social post or a QR code. Add
              parameters for your hidden values, e.g.{' '}
              <span className="api-inline">?utm_source=newsletter</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Embed</div>
            <div className="dp-field-v">
              Two lines of HTML for any page of your site. The form sizes itself to its content and
              picks up the page&rsquo;s own parameters for hidden values. Its colours, corners and
              font can match your site.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Your own design</div>
            <div className="dp-field-v">
              Copy the prompt under &ldquo;Build it into your own site&rdquo; into a coding agent
              (Claude Code, Cursor, Lovable and others). It describes every question and the exact
              answers Revvly accepts, so the agent can build the form in your site&rsquo;s own
              design and post it to Revvly. The reference is in the{' '}
              <Link to="/docs/api/forms">Forms API</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">From your server</div>
            <div className="dp-field-v">
              Submit from your own backend with an API key that can submit forms, for example when
              your site already posts forms to your server. See{' '}
              <Link to="/docs/api/forms#submit-form">submitForm</Link>.
            </div>
          </div>
        </div>
        <span className="api-code-label">Embed snippet</span>
        <pre className="api-code">{EMBED}</pre>
        <p>
          An embedded form can match your site&rsquo;s colours, corners and font. See{' '}
          <a href="#style-embed">match the embed to your site</a>.
        </p>
        <p>
          The script and the form itself are both served by the Revvly dashboard,{' '}
          <span className="api-inline">app.revvly.ai</span>: the script replaces the placeholder
          with a frame of the hosted form page, so nothing else on your site talks to Revvly. If
          your pages send a Content Security Policy, allow{' '}
          <span className="api-inline">https://app.revvly.ai</span> under{' '}
          <span className="api-inline">script-src</span> (the script) and{' '}
          <span className="api-inline">frame-src</span> (the form). The form&rsquo;s own requests
          happen inside that frame, so they need no <span className="api-inline">connect-src</span>{' '}
          entry on your page.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="style-embed">Match the embed to your site</AnchorHeading>
        <p>
          By default an embedded form is light, with a mint button. To match your site, add{' '}
          <code>data-revvly-*</code> attributes to the same <code>div</code>. The form itself is not
          changed, so every site that embeds it can dress it differently, and a form with no
          attributes looks exactly as before. Only embedded forms read them; the hosted link keeps
          Revvly&rsquo;s look.
        </p>
        <span className="api-code-label">A dark form on a black page</span>
        <pre className="api-code">{THEMED_EMBED}</pre>
        <p>
          <code>data-revvly-theme</code> also takes one JSON object, and a separate attribute wins
          over the same key in it. Use the JSON form when you would rather keep the theme in one
          place, and a gradient on the button when a flat colour is not enough.
        </p>
        <pre className="api-code">{THEMED_JSON}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">data-revvly-theme</div>
            <div className="dp-field-v">
              <span className="api-inline">dark</span> or <span className="api-inline">light</span>{' '}
              (the default), or a JSON object. Set it to dark whenever the page behind the form is
              dark.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-background, -card</div>
            <div className="dp-field-v">
              The page behind the form, and the form&rsquo;s own surface. Use{' '}
              <span className="api-inline">transparent</span> for the page so the form sits directly
              on yours. The card is transparent unless you set it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-text, -label, -muted</div>
            <div className="dp-field-v">
              The title and answers, the field labels, and help text and placeholders.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-input-background, -input-border, -input-focus-border</div>
            <div className="dp-field-v">The input boxes, at rest and when selected.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-accent, -accent-text</div>
            <div className="dp-field-v">
              The submit button, focus ring, links and selected options, and the text on the button.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-button-background</div>
            <div className="dp-field-v">
              A colour or a <span className="api-inline">linear-gradient(…)</span> for the submit
              button, when it should be more than the accent. Set <em>-accent-text</em> with it so
              the label stays readable.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-error</div>
            <div className="dp-field-v">Error messages and required markers.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-radius, -button-radius</div>
            <div className="dp-field-v">
              Corner radius of the inputs and of the button, in pixels. A large button radius makes
              a pill.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">-font</div>
            <div className="dp-field-v">
              A font family the visitor&rsquo;s device already has, such as{' '}
              <span className="api-inline">Inter, system-ui, sans-serif</span>. The form runs in its
              own frame and cannot load your site&rsquo;s font files.
            </div>
          </div>
        </div>
        <p>
          Colours are hex, <code>rgb()</code>, <code>hsl()</code> or <code>transparent</code>.
          Anything that is not in one of those forms is ignored, so a typo leaves that part of the
          form on its default rather than breaking it.
        </p>

        <h3>Ask your coding agent to do it</h3>
        <p>
          If a coding agent (Claude Code, Cursor, Lovable and others) adds the form to your site, it
          should ask you before it restyles anything, and it should read your site&rsquo;s own
          colours instead of guessing. Copy this prompt, with your form&rsquo;s id and dashboard
          address from its Share tab:
        </p>
        <span className="api-code-label">Add the form, then offer to style it</span>
        <pre className="api-code api-code-prompt">{AGENT_EMBED_PROMPT}</pre>
        <p>
          For a form that is already on your site, use this shorter one. It asks the same question
          first and changes only the attributes:
        </p>
        <span className="api-code-label">Restyle an embedded form</span>
        <pre className="api-code api-code-prompt">{AGENT_RESTYLE_PROMPT}</pre>
        <p>
          If you write your own agent instructions, keep the question: whether to keep the default
          look or match the site is the customer&rsquo;s choice, and a form that silently takes on
          guessed colours is harder to undo than one that asked.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="submissions">Step 5: Read the submissions</AnchorHeading>
        <UiPath
          steps={['Library', 'Forms', 'Your form', 'Submissions']}
          note="Newest first, searchable by email and filterable by follow-up status. Open one to see the answers, download files, and follow each step."
        />
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Follow-up status</div>
            <div className="dp-field-v">
              <em>Running</em> while steps run or wait to retry, <em>Done</em> when all ran,{' '}
              <em>Failed</em> when a step failed for good (open it to see why, fix the cause and
              press <strong>Retry failed steps</strong>), and <em>Skipped</em> for spam.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Export CSV</div>
            <div className="dp-field-v">
              Exports what the current search and filter show, up to the newest 10,000, with one
              column per question across every version of the form. Opens cleanly in Excel and
              Google Sheets.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Ask the Assistant</div>
            <div className="dp-field-v">
              The <Link to="/docs/assistant">Revvly Assistant</Link> and{' '}
              <Link to="/docs/api/mcp">MCP clients</Link> can list your forms and read their
              submissions: &ldquo;How many demo requests came in this week, and from which
              companies?&rdquo;
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Delete</div>
            <div className="dp-field-v">
              Deleting a submission also deletes its uploaded files. Leads, emails and Slack
              messages it already created stay where they are.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="client-links">Send a form to a client</AnchorHeading>
        <UiPath
          steps={['Clients', 'Your client', 'Forms', 'Send a form']}
          note="The same Forms tab is on a prospect. Choose a published form, then Make link."
        />
        <p className="dp-section-sub">
          A form can also collect details from one client or prospect you already know: their
          billing address, who receives invoices, who signs the contract. You build it like any
          other form, then send that client a link made for them alone. Their answers come back to
          the record&rsquo;s Forms tab, where you decide what to copy onto the record.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Client fields</div>
            <div className="dp-field-v">
              In the builder, <strong>Add client field</strong> adds a ready-made question, or set{' '}
              <strong>Client field</strong> in a question&rsquo;s settings: Billing address, Tax ID,
              Legal name, Legal structure, Company number, the invoice contact&rsquo;s name, email
              and phone, or the contract signer&rsquo;s name, email and phone. Each client field can
              be used by one question per form.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Only through client links</div>
            <div className="dp-field-v">
              A form setting. When it is ticked, the public link and the embed stop working, and the
              form opens only through links made for a client.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">The link</div>
            <div className="dp-field-v">
              Made for that client only, and opens with the client fields filled with what the
              record holds now. It can be opened as often as they like until they submit, and stops
              working once answered. Anyone who has it can answer, so send it only to them.{' '}
              <strong>Revoke link</strong> takes back a link that has not been answered.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">From an email</div>
            <div className="dp-field-v">
              In the email composer on a client or prospect, the <strong>Form link</strong> menu
              inserts a link already sent and still waiting for answers, or makes a new one for any
              published form. Nothing is sent until you send the email.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Status</div>
            <div className="dp-field-v">
              <em>Waiting for answers</em> until the client submits, with <em>Not opened yet</em> or
              how many times it was opened. Then <em>1 change to review</em> (or more) while answers
              differ from the record, and <em>Answered</em> once nothing is left to review. A form
              the client filled in on your website shows as <em>Received</em>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Apply</div>
            <div className="dp-field-v">
              Open an answered link to see each answer beside what you hold now, with an{' '}
              <strong>Apply</strong> button for each, or <strong>Apply all</strong>. Nothing is
              written to the record automatically, and a blank answer never clears what you hold.
              Answers from a public form are shown but cannot be applied, because anyone can fill
              one in and name any company.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Proposed subscription</div>
            <div className="dp-field-v">
              The Plan, Billing period and Subscription start date client fields describe a
              subscription. For a plan or billing period you choose what each option stands for. The
              answers show as a <strong>Proposed subscription</strong>, and{' '}
              <strong>Create subscription</strong> opens the usual subscription wizard filled in
              with them; nothing is created until you confirm it there. A plan option follows the
              plan, so it always proposes the version on sale today, even after you republish the
              plan.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="limits">Spam and limits</AnchorHeading>
        <ol className="dp-connects">
          <li>
            Every form carries an invisible spam trap. A bot that fills it sees the normal thank-you
            message, and nothing is stored or sent.
          </li>
          <li>
            Each form takes up to 1,000 submissions a day from its link, embed and your own site,
            and a separate 10,000 a day through the API, so a flood on the public form cannot block
            your backend. Past the limit, submissions are refused until the next day (UTC).
          </li>
          <li>Each visitor can submit up to 30 times a minute; more is refused for a minute.</li>
          <li>
            Uploaded files are checked for their real type and scanned for viruses before they are
            kept. Files that are never submitted are deleted after a day.
          </li>
          <li>
            Visitors&rsquo; IP addresses are never stored, only a keyed fingerprint used to spot
            abuse.
          </li>
        </ol>
      </section>

      <section className="dp-section">
        <AnchorHeading id="checklist">Before you go live</AnchorHeading>
        <ol className="dp-connects">
          <li>Submit the published form once yourself and check every step in its log.</li>
          <li>
            The questions that identify the person have roles, and the email question is required if
            you create leads or reply to the submitter.
          </li>
          <li>The Slack channel and the mailbox you send from are the ones you expect.</li>
          <li>
            If you embed the form on a dark or branded page, it is themed to match and you looked at
            it on a phone. See <a href="#style-embed">match the embed to your site</a>.
          </li>
          <li>Links in your campaigns carry the parameters your hidden values read.</li>
          <li>
            If you send the form to clients, its client fields are set, you opened a test link
            yourself and saw it filled with the record&rsquo;s details, and you chose whether it
            should open only through client links. See{' '}
            <a href="#client-links">send a form to a client</a>.
          </li>
          <li>
            If you collect personal data, the form has a consent checkbox or a paragraph pointing to
            your privacy notice.
          </li>
        </ol>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api/forms">Forms API reference ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/crm">Run your sales pipeline ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/api-keys">Configure integration API keys ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
